import { unsafeCSS, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { BaseElement, customElement, watch } from '../../base-element';
import type { SegmentedControlItemWC } from './segmented-control-item.wc';
import styles from './segmented-control.scss?inline';

/**
 * A segmented (pill) single-select control — e.g. a Sandbox / Live switch.
 * Holds a `value` and toggles the matching slotted `ps-segmented-control-item`.
 */
@customElement('ps-segmented-control')
export class SegmentedControlWC extends BaseElement {
  static styles = unsafeCSS(styles);

  @query('slot') defaultSlot: HTMLSlotElement;

  /** The value of the currently selected segment. */
  @property({ reflect: true }) value = '';

  private getItems(): SegmentedControlItemWC[] {
    return (
      (this.defaultSlot?.assignedElements({ flatten: true }) ??
        []) as HTMLElement[]
    ).filter(
      (el) => el.tagName.toLowerCase() === 'ps-segmented-control-item'
    ) as SegmentedControlItemWC[];
  }

  private syncSelection() {
    this.getItems().forEach((item) => {
      item.toggleAttribute('selected', item.value === this.value);
    });
  }

  @watch('value')
  handleValueChange() {
    this.syncSelection();
  }

  private handleSlotChange() {
    const items = this.getItems();
    // Default to the first segment when no value is provided.
    if (!this.value && items.length > 0) {
      this.value = items[0].value;
    }
    this.syncSelection();
  }

  private handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const item = target.closest(
      'ps-segmented-control-item'
    ) as SegmentedControlItemWC | null;

    if (!item || item.disabled || item.value === this.value) {
      return;
    }

    this.value = item.value;
    this.emit('change', { detail: { value: this.value } });
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      return;
    }

    const items = this.getItems().filter((item) => !item.disabled);
    if (items.length === 0) {
      return;
    }

    event.preventDefault();
    const currentIndex = items.findIndex((item) => item.value === this.value);
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = items[(currentIndex + delta + items.length) % items.length];

    if (next && next.value !== this.value) {
      this.value = next.value;
      this.emit('change', { detail: { value: this.value } });
    }
  }

  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    this.setAttribute('role', 'radiogroup');
  }

  render() {
    return html`
      <div class="c-segmented-control" part="base">
        <slot
          @slotchange=${this.handleSlotChange}
          @click=${this.handleClick}
          @keydown=${this.handleKeyDown}
        ></slot>
      </div>
    `;
  }
}

export type SegmentedControlChangeEvent = CustomEvent<{ value: string }>;

declare global {
  interface HTMLElementTagNameMap {
    'ps-segmented-control': SegmentedControlWC;
  }
  enum PSElementTagNameMap {
    'ps-segmented-control' = 'ps-segmented-control',
  }
}
